/**
 * Offline Queue Hook
 *
 * Manages offline data queueing with IndexedDB persistence.
 * Wraps pure utility functions with React state and IndexedDB storage.
 *
 * Features:
 * - Persistent queue storage via IndexedDB
 * - Automatic sync when online
 * - Network status detection
 * - Optimistic updates support
 * - Failed operation retry with exponential backoff
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  type QueuedOperation,
  type OfflineOperationType,
  type QueueStats,
  createQueuedOperation,
  markSyncing,
  markSynced,
  markFailed,
  calculateQueueStats,
  getPendingOperations,
  hasUnsyncedItems,
  hasFailedItems,
  shouldAttemptSync,
  MAX_RETRY_ATTEMPTS,
} from "@/lib/offline-queue-utils";

// ============================================================================
// Constants
// ============================================================================

const DB_NAME = "floline-offline-queue";
const DB_VERSION = 1;
const STORE_NAME = "operations";
const SYNC_DEBOUNCE_MS = 1000;
const RETRY_BASE_DELAY_MS = 1000;

// ============================================================================
// Types
// ============================================================================

/**
 * Sync handler function type.
 * Returns true if sync succeeded, false otherwise.
 */
export type SyncHandler<T> = (operation: QueuedOperation<T>) => Promise<boolean>;

/**
 * Registry of sync handlers by operation type
 */
export type SyncHandlerRegistry = Partial<
  Record<OfflineOperationType, SyncHandler<unknown>>
>;

/**
 * Hook options
 */
export interface UseOfflineQueueOptions {
  /** Custom sync handlers for each operation type */
  syncHandlers?: SyncHandlerRegistry;
  /** Whether to auto-sync when online (default: true) */
  autoSync?: boolean;
  /** Debounce time in ms before syncing (default: 1000) */
  syncDebounceMs?: number;
}

/**
 * Hook return type
 */
export interface UseOfflineQueueReturn {
  /** Current queue of operations */
  queue: QueuedOperation<unknown>[];
  /** Queue statistics */
  stats: QueueStats;
  /** Whether the device is online */
  isOnline: boolean;
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
  /** Whether there are unsynced items */
  hasUnsynced: boolean;
  /** Whether there are failed items */
  hasFailed: boolean;
  /** Whether the queue is loading from IndexedDB */
  isLoading: boolean;
  /** Error if queue loading failed */
  error: Error | null;
  /** Add an operation to the queue */
  enqueue: <T>(type: OfflineOperationType, payload: T) => Promise<QueuedOperation<T>>;
  /** Manually trigger sync */
  syncNow: () => Promise<void>;
  /** Retry a specific failed operation */
  retryOperation: (operationId: string) => Promise<void>;
  /** Clear all synced operations from queue */
  clearSynced: () => Promise<void>;
  /** Clear all operations from queue */
  clearAll: () => Promise<void>;
}

// ============================================================================
// IndexedDB Helpers
// ============================================================================

/**
 * Open the IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        // Index for querying by status
        store.createIndex("status", "status", { unique: false });
        // Index for querying by type
        store.createIndex("type", "type", { unique: false });
        // Index for ordering by queue time
        store.createIndex("queuedAt", "queuedAt", { unique: false });
      }
    };
  });
}

/**
 * Get all operations from the database
 */
async function getAllOperations(): Promise<QueuedOperation<unknown>[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = () => {
      reject(new Error(`Failed to get operations: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result as QueuedOperation<unknown>[]);
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
}

/**
 * Add or update an operation in the database
 */
async function putOperation<T>(
  operation: QueuedOperation<T>
): Promise<QueuedOperation<T>> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(operation);

    request.onerror = () => {
      reject(new Error(`Failed to put operation: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(operation);
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
}

/**
 * Delete an operation from the database
 */
export async function deleteOperation(operationId: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(operationId);

    request.onerror = () => {
      reject(new Error(`Failed to delete operation: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve();
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
}

/**
 * Delete operations by status
 */
async function deleteByStatus(status: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("status");
    const request = index.openCursor(IDBKeyRange.only(status));

    request.onerror = () => {
      reject(new Error(`Failed to delete by status: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * Clear all operations from the database
 */
async function clearAllOperations(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onerror = () => {
      reject(new Error(`Failed to clear operations: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve();
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing offline operation queue with IndexedDB persistence.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const {
 *     queue,
 *     stats,
 *     isOnline,
 *     isSyncing,
 *     enqueue,
 *     syncNow,
 *   } = useOfflineQueue({
 *     syncHandlers: {
 *       create_pain_entry: async (op) => {
 *         await api.createPainEntry(op.payload);
 *         return true;
 *       },
 *     },
 *   });
 *
 *   const handleCreate = async (data: PainEntryPayload) => {
 *     // Queue for offline sync
 *     await enqueue('create_pain_entry', data);
 *   };
 * }
 * ```
 */
export function useOfflineQueue(
  options: UseOfflineQueueOptions = {}
): UseOfflineQueueReturn {
  const {
    syncHandlers = {},
    autoSync = true,
    syncDebounceMs = SYNC_DEBOUNCE_MS,
  } = options;

  // State
  const [queue, setQueue] = useState<QueuedOperation<unknown>[]>([]);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Refs for sync debouncing
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncHandlersRef = useRef(syncHandlers);
  syncHandlersRef.current = syncHandlers;

  // Derived state
  const stats = calculateQueueStats(queue);
  const hasUnsynced = hasUnsyncedItems(queue);
  const hasFailed = hasFailedItems(queue);

  // Load queue from IndexedDB on mount
  useEffect(() => {
    let mounted = true;

    async function loadQueue() {
      try {
        const operations = await getAllOperations();
        if (mounted) {
          setQueue(operations);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to load queue")
          );
          setIsLoading(false);
        }
      }
    }

    loadQueue();

    return () => {
      mounted = false;
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Sync a single operation
  const syncOperation = useCallback(
    async (operation: QueuedOperation<unknown>): Promise<boolean> => {
      const handler = syncHandlersRef.current[operation.type];
      if (!handler) {
        console.warn(`No sync handler for operation type: ${operation.type}`);
        return false;
      }

      // Mark as syncing
      const syncingOp = markSyncing(operation);
      await putOperation(syncingOp);
      setQueue((prev) =>
        prev.map((op) => (op.id === syncingOp.id ? syncingOp : op))
      );

      try {
        const success = await handler(operation);

        if (success) {
          // Mark as synced
          const syncedOp = markSynced(syncingOp);
          await putOperation(syncedOp);
          setQueue((prev) =>
            prev.map((op) => (op.id === syncedOp.id ? syncedOp : op))
          );
          return true;
        }

        // Mark as failed
        const failedOp = markFailed(syncingOp, "Sync returned false");
        await putOperation(failedOp);
        setQueue((prev) =>
          prev.map((op) => (op.id === failedOp.id ? failedOp : op))
        );
        return false;
      } catch (err) {
        // Mark as failed with error message
        const errorMessage =
          err instanceof Error ? err.message : "Unknown sync error";
        const failedOp = markFailed(syncingOp, errorMessage);
        await putOperation(failedOp);
        setQueue((prev) =>
          prev.map((op) => (op.id === failedOp.id ? failedOp : op))
        );
        return false;
      }
    },
    []
  );

  // Sync all pending operations
  const syncNow = useCallback(async () => {
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);

    try {
      const pending = getPendingOperations(queue);

      for (const operation of pending) {
        // Skip if exceeded max retries
        if (operation.attemptCount >= MAX_RETRY_ATTEMPTS) {
          continue;
        }

        // Add exponential backoff delay between retries
        if (operation.attemptCount > 0) {
          const delay = RETRY_BASE_DELAY_MS * (2 ** (operation.attemptCount - 1));
          await new Promise((resolve) => setTimeout(resolve, delay));
        }

        await syncOperation(operation);

        // Check if we went offline during sync
        if (!navigator.onLine) {
          break;
        }
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, queue, syncOperation]);

  // Auto-sync when online and have pending items
  useEffect(() => {
    if (!autoSync || isLoading) return;

    // Clear any existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    // Schedule sync if conditions are met
    if (shouldAttemptSync(isOnline, hasUnsynced)) {
      syncTimeoutRef.current = setTimeout(() => {
        syncNow();
      }, syncDebounceMs);
    }

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [autoSync, isOnline, hasUnsynced, isLoading, syncDebounceMs, syncNow]);

  // Enqueue a new operation
  const enqueue = useCallback(
    async <T>(
      type: OfflineOperationType,
      payload: T
    ): Promise<QueuedOperation<T>> => {
      const operation = createQueuedOperation(type, payload);

      // Save to IndexedDB
      await putOperation(operation);

      // Update local state
      setQueue((prev) => [...prev, operation as QueuedOperation<unknown>]);

      return operation;
    },
    []
  );

  // Retry a specific failed operation
  const retryOperation = useCallback(
    async (operationId: string) => {
      const operation = queue.find((op) => op.id === operationId);
      if (!operation) return;

      // Reset attempt count for manual retry
      const resetOp: QueuedOperation<unknown> = {
        ...operation,
        status: "pending",
        attemptCount: 0,
        errorMessage: undefined,
      };

      await putOperation(resetOp);
      setQueue((prev) =>
        prev.map((op) => (op.id === resetOp.id ? resetOp : op))
      );

      // Trigger sync
      if (isOnline) {
        await syncOperation(resetOp);
      }
    },
    [queue, isOnline, syncOperation]
  );

  // Clear synced operations
  const clearSynced = useCallback(async () => {
    await deleteByStatus("synced");
    setQueue((prev) => prev.filter((op) => op.status !== "synced"));
  }, []);

  // Clear all operations
  const clearAll = useCallback(async () => {
    await clearAllOperations();
    setQueue([]);
  }, []);

  return {
    queue,
    stats,
    isOnline,
    isSyncing,
    hasUnsynced,
    hasFailed,
    isLoading,
    error,
    enqueue,
    syncNow,
    retryOperation,
    clearSynced,
    clearAll,
  };
}

/**
 * Hook for checking online status only (lighter weight)
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}
